import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, Modal } from "semantic-ui-react";

import { useSelectedDealer } from "components";
import { usePermanentRealTimeUser } from "hooks";
import { createFormHelper } from "lib/Form";
import { useCreateQuestionMutation } from "modules/QuestionsDashboard/components/CreateQuestionModal/hooks";
import { useDeleteQuestionMutation } from "modules/QuestionsDashboard/components/EditQuestionModal/components/EditQuestionActions/hooks";
import { useUpdateQuestionMutation } from "modules/QuestionsDashboard/components/EditQuestionModal/hooks";
import { isAdmin } from "utils/common";

import type { DealerID, Question, QuestionElement, QuestionID } from "models";

type CopyQuestionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onCopy: (questionID: QuestionID) => void;
};

const FormHelper = createFormHelper<Question>();

export const CopyQuestionModal = ({ isOpen, onClose, onCopy }: CopyQuestionModalProps) => {
  const { t } = useTranslation();

  const [notSelectedElements, setNotSelectedElements] = useState<number[]>([]);

  const { getValues } = FormHelper.useFormContext();
  const questionElements = FormHelper.useWatch("question_elements") ?? [];
  const { user } = usePermanentRealTimeUser();
  const { selectedDealer } = useSelectedDealer();
  const { mutateAsync: createQuestion, isPending: isCreatePending } = useCreateQuestionMutation();
  const { mutateAsync: updateQuestion, isPending: isUpdatePending } = useUpdateQuestionMutation();
  const { mutateAsync: deleteQuestion } = useDeleteQuestionMutation();

  const isCopyPending = isCreatePending || isUpdatePending;

  const handleClose = () => {
    setNotSelectedElements([]);
    onClose();
  };

  const handleToggleElement = (elementIdx: number) =>
    setNotSelectedElements(
      notSelectedElements.includes(elementIdx) ? notSelectedElements.filter(notSelectedIdx => notSelectedIdx !== elementIdx) : [...notSelectedElements, elementIdx]
    );

  const handleCopy = async (dealerId: DealerID | null) => {
    const question = getValues();
    const copiedElements = questionElements.reduce<QuestionElement[]>(
      (selectedElements, questionElement, elementIdx) =>
        notSelectedElements.includes(elementIdx) ? selectedElements : [...selectedElements, { ...questionElement, ordering: selectedElements.length }],
      []
    );

    const title = `${question.title ?? ""} (${t("v8_copy", "Copy")})`;
    const { id } = await createQuestion({ title, dealer_id: dealerId });

    try {
      await updateQuestion({ ...question, id, title, dealer_id: dealerId, question_elements: copiedElements });
    } catch {
      await deleteQuestion({ id });
      return;
    }

    setNotSelectedElements([]);
    onCopy(id);
  };

  return (
    <Modal size="small" className="CopyQuestionModal" open={isOpen} onClose={handleClose} closeOnDimmerClick={false} closeIcon>
      <Modal.Header>{t("v8_select_elements_to_copy", "Select elements to copy")}</Modal.Header>

      <Modal.Content>
        <div className="copy-question-elements">
          {questionElements.map((questionElement, elementIdx) => (
            <Checkbox
              key={`copy-element-${elementIdx}`}
              label={questionElement.name}
              checked={!notSelectedElements.includes(elementIdx)}
              onChange={() => handleToggleElement(elementIdx)}
            />
          ))}
        </div>

        <h4>{t("v8_copy_question_to", "Copy question to")}</h4>
        <div className="copy-question-targets">
          {isAdmin(user?.role_id ?? 0) && (
            <Button type="button" color="orange" disabled={isCopyPending} loading={isCopyPending} onClick={() => handleCopy(null)}>
              {t("v8_default", "Default")}
            </Button>
          )}

          {selectedDealer && (
            <Button type="button" color="orange" disabled={isCopyPending} loading={isCopyPending} onClick={() => handleCopy(selectedDealer.id)}>
              {t("v8_dealer", "Dealer")}
            </Button>
          )}
        </div>
      </Modal.Content>
    </Modal>
  );
};
